import React, { useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Building2, Calendar, LayoutDashboard, LifeBuoy, Medal, MessageSquareText, Settings, Star, UserRound, Users } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import Brand from '../Brand';
import './Sidebar.css';

const navigation = {
  Master: [
    ['/panel', LayoutDashboard, 'Panel'],
    ['/members', Users, 'Members'],
    ['/ranking', Medal, 'Ranking'],
    ['/centers', Building2, 'Centros'],
    ['/community', MessageSquareText, 'Sociales'],
    ['/administration', Settings, 'Administración'], ['/support', LifeBuoy, 'Soporte'],
  ],
  Propietario: [
    ['/panel', LayoutDashboard, 'Panel'], ['/schedule', Calendar, 'Clases'], ['/programming', Calendar, 'Programación'], ['/members', Users, 'Miembros'],
    ['/community', MessageSquareText, 'Comunidad'], ['/administration', Settings, 'Administración'],
  ],
  Alumno: [
    ['/panel', LayoutDashboard, 'Panel'], ['/schedule', Calendar, 'Clases'],
    ['/my-centers', Building2, 'Centros'], ['/community', MessageSquareText, 'Sociales'],
  ],
  Profesor: [
    ['/panel', UserRound, 'Panel'], ['/schedule', Calendar, 'Mis clases'], ['/community', MessageSquareText, 'Resultados'],
    ['/ranking', Star, 'Ranking'], ['/support', LifeBuoy, 'Soporte'],
  ],
  Administrador: [
    ['/panel', LayoutDashboard, 'Panel'], ['/schedule', Calendar, 'Clases'], ['/programming', Calendar, 'Programación'], ['/members', Users, 'Miembros'],
    ['/community', MessageSquareText, 'Comunidad'], ['/administration', Settings, 'Administración'],
  ],
  Recepción: [['/panel', LayoutDashboard, 'Panel'], ['/members', Users, 'Miembros'], ['/schedule', Calendar, 'Clases'], ['/support', LifeBuoy, 'Soporte']],
};

export const Sidebar = () => {
  const { user } = useAuth();
  const [pendingRequests, setPendingRequests] = useState(0);
  const [commentNotifications, setCommentNotifications] = useState(0);
  const [
    socialMessageNotifications,
    setSocialMessageNotifications
  ] = useState(0);

  // ===== PULSORA MASTER MODERATION BADGE V1 =====
  const [
    pendingModerationReports,
    setPendingModerationReports
  ] = useState(0);

  const isPersonalProfile = !user?.global_access && Boolean(user?.member_id) && !user?.active_center;
  const isCenterWorkspace = !user?.global_access && Boolean(user?.active_center);
  const items = user?.global_access ? navigation.Master : (isPersonalProfile ? navigation.Alumno : (navigation[user?.role] || []));
  const canReviewRequests = Boolean(user?.global_access || ['Propietario', 'Administrador'].includes(user?.role));
  const activeCenterId = user?.active_center?.centro_id;
  const centerMobileItems = activeCenterId ? [
    ['/panel', LayoutDashboard, 'Panel'],
    ['/community', MessageSquareText, 'Comunidad'],
    ['/administration', Settings, 'Administración'],
    [`/center-profile/${activeCenterId}`, UserRound, 'Perfil'],
  ] : [];

  const loadPendingRequests = useCallback(async () => {
    if (!canReviewRequests || (!user?.global_access && !activeCenterId)) {
      setPendingRequests(0);
      return;
    }
    try {
      const data = await api.joinRequests.getAll();
      const rows = Array.isArray(data) ? data : data.results || [];
      setPendingRequests(rows.filter(row => row.estado === 'aprobada' && !row.visto_por_centro).length);
    } catch (_) {
      // El contador no debe interrumpir la navegación si el servidor está despertando.
    }
  }, [activeCenterId, canReviewRequests, user?.global_access]);

  const loadCommentNotifications = useCallback(async () => {
    if (user?.global_access) { setCommentNotifications(0); return; }
    if (user?.role !== 'Alumno' && !activeCenterId) { setCommentNotifications(0); return; }
    try {
      const data = await api.notifications.getAttention();
      setCommentNotifications(Number(data?.comments || 0));
    } catch (_) {
      // Los avisos de navegación no deben bloquear la experiencia.
    }
  }, [activeCenterId, user?.global_access, user?.role]);

  useEffect(() => {
    loadPendingRequests();
    const refresh = () => loadPendingRequests();
    window.addEventListener('pulsora:join-requests-updated', refresh);
    const interval = window.setInterval(refresh, 30000);
    return () => {
      window.removeEventListener('pulsora:join-requests-updated', refresh);
      window.clearInterval(interval);
    };
  }, [loadPendingRequests]);

  useEffect(() => {
    loadCommentNotifications();
    const refresh = () => loadCommentNotifications();
    window.addEventListener('pulsora:attention-updated', refresh);
    window.addEventListener('pulsora:notifications-updated', refresh);
    const interval = window.setInterval(refresh, 20000);
    return () => {
      window.removeEventListener('pulsora:attention-updated', refresh);
      window.removeEventListener('pulsora:notifications-updated', refresh);
      window.clearInterval(interval);
    };
  }, [loadCommentNotifications]);


  const loadSocialMessageNotifications =
    useCallback(async () => {
      try {
        const data =
          await api.social.notifications();

        setSocialMessageNotifications(
          Number(data?.total || 0)
        );
      } catch (_) {
        // La navegación continúa aunque el contador falle.
      }
    }, []);

  useEffect(() => {
    loadSocialMessageNotifications();

    const refresh = () =>
      loadSocialMessageNotifications();

    window.addEventListener(
      'pulsora:social-updated',
      refresh
    );

    const interval = window.setInterval(
      refresh,
      5000
    );

    return () => {
      window.removeEventListener(
        'pulsora:social-updated',
        refresh
      );

      window.clearInterval(interval);
    };
  }, [loadSocialMessageNotifications]);

    const loadModerationNotifications =
    useCallback(async () => {
      if (!user?.global_master) {
        setPendingModerationReports(0);
        return;
      }

      try {
        const data =
          await api.social.moderation.summary();

        setPendingModerationReports(
          Number(data?.pending || 0)
        );
      } catch (_) {
        /*
         * El contador de moderación nunca debe
         * bloquear la navegación del Master.
         */
      }
    }, [user?.global_master]);

  useEffect(() => {
    loadModerationNotifications();

    if (!user?.global_master) {
      return undefined;
    }

    const refresh = () =>
      loadModerationNotifications();

    window.addEventListener(
      'pulsora:moderation-updated',
      refresh
    );

    const interval = window.setInterval(
      refresh,
      5000
    );

    return () => {
      window.removeEventListener(
        'pulsora:moderation-updated',
        refresh
      );

      window.clearInterval(interval);
    };
  }, [
    loadModerationNotifications,
    user?.global_master,
  ]);

const socialNotificationsTotal =
    commentNotifications +
    socialMessageNotifications;

const renderItems = navItems => navItems.map(([to, Icon, label, disabled]) => <li key={to} className={to === '/panel' ? 'panel-nav-item' : ''}>{disabled ? <span className="nav-link disabled" aria-disabled="true"><Icon size={20}/><span>{label}</span></span> : <NavLink to={to} className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}><Icon size={20}/><span>{label}</span>{pendingRequests > 0 && to === '/members' && !user?.global_access && <strong className="nav-pending-badge" aria-label={`${pendingRequests} miembros nuevos`}>{pendingRequests > 99 ? '99+' : pendingRequests}</strong>}{socialNotificationsTotal > 0 &&
  to === '/community' &&
  !user?.global_access && (
    <strong
      className="nav-pending-badge"
      aria-label={`${socialNotificationsTotal} notificaciones sociales`}
    >
      {socialNotificationsTotal > 99
        ? '99+'
        : socialNotificationsTotal}
    </strong>
  )}{pendingModerationReports > 0 &&
  to === '/administration' &&
  user?.global_master && (
    <strong
      className="nav-pending-badge"
      aria-label={`${pendingModerationReports} denuncias pendientes`}
      title={`${pendingModerationReports} denuncias pendientes`}
    >
      {pendingModerationReports > 99
        ? '99+'
        : pendingModerationReports}
    </strong>
  )}</NavLink>}</li>);

  return <aside className={`sidebar${isPersonalProfile ? ' member-sidebar' : ''}${isCenterWorkspace ? ' center-sidebar' : ''}`}><div className="sidebar-header"><NavLink to="/panel" className="sidebar-home-link" aria-label="Ir al Panel"><Brand light/></NavLink></div><nav className="sidebar-nav"><ul className={isCenterWorkspace ? 'center-desktop-nav' : ''}>{renderItems(items)}</ul>{isCenterWorkspace && <ul className="center-mobile-nav center-mobile-only-nav">{renderItems(centerMobileItems)}</ul>}</nav></aside>;
};
